import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ClipboardList, ArrowLeft, Filter, Search, Building2, User,
  Wallet, Tag, CreditCard, FileText, Trash2, Edit2, Plus, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { getAuditLogs, getCompanies } from '../utils/api';

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getActionColor = (action) => {
  switch (action) {
    case 'create': return 'bg-emerald-100 text-emerald-700';
    case 'update': return 'bg-blue-100 text-blue-700';
    case 'delete': return 'bg-red-100 text-red-700';
    case 'import': return 'bg-purple-100 text-purple-700';
    default: return 'bg-stone-100 text-stone-700';
  }
};

const getEntityIcon = (entityType) => {
  switch (entityType) {
    case 'company': return Building2;
    case 'user': return User;
    case 'wallet': return Wallet;
    case 'category': return Tag;
    case 'transaction': return CreditCard;
    case 'data': return Download;
    default: return FileText;
  }
};

const getActionIcon = (action) => {
  switch (action) {
    case 'create': return Plus;
    case 'update': return Edit2;
    case 'delete': return Trash2;
    case 'import': return Download;
    default: return FileText;
  }
};

export const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    company_id: '',
    entity_type: '',
    action: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const activeFilters = {};
      if (filters.company_id) activeFilters.company_id = filters.company_id;
      if (filters.entity_type) activeFilters.entity_type = filters.entity_type;
      if (filters.action) activeFilters.action = filters.action;
      
      const [logsData, companiesData] = await Promise.all([
        getAuditLogs(activeFilters),
        getCompanies()
      ]);
      setLogs(logsData);
      setCompanies(companiesData);
    } catch (error) {
      toast.error('Failed to load audit logs: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clearFilters = () => {
    setFilters({ company_id: '', entity_type: '', action: '' });
  };

  const hasActiveFilters = filters.company_id || filters.entity_type || filters.action;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/admin" className="p-2 hover:bg-stone-100 rounded-lg">
                <ArrowLeft className="w-5 h-5 text-stone-600" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-emerald-900 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5" />
                  Audit Logs
                </h1>
                <p className="text-sm text-stone-500">Track all system activities</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="bg-white rounded-xl border border-stone-200 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-stone-500" />
            <span className="font-medium text-stone-700">Filters</span>
            {hasActiveFilters && (
              <button 
                onClick={clearFilters}
                className="text-sm text-emerald-600 hover:text-emerald-700 ml-auto"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-sm text-stone-600">Company</Label>
              <Select 
                value={filters.company_id || "all"} 
                onValueChange={(value) => setFilters({ ...filters, company_id: value === "all" ? "" : value })}
              >
                <SelectTrigger data-testid="filter-company">
                  <SelectValue placeholder="All companies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All companies</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-stone-600">Entity Type</Label>
              <Select 
                value={filters.entity_type || "all"} 
                onValueChange={(value) => setFilters({ ...filters, entity_type: value === "all" ? "" : value })}
              >
                <SelectTrigger data-testid="filter-entity">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="company">Company</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="wallet">Wallet</SelectItem>
                  <SelectItem value="category">Category</SelectItem>
                  <SelectItem value="transaction">Transaction</SelectItem>
                  <SelectItem value="data">Data Import</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-stone-600">Action</Label>
              <Select 
                value={filters.action || "all"} 
                onValueChange={(value) => setFilters({ ...filters, action: value === "all" ? "" : value })}
              >
                <SelectTrigger data-testid="filter-action">
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  <SelectItem value="create">Create</SelectItem>
                  <SelectItem value="update">Update</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                  <SelectItem value="import">Import</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Logs List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="bg-white rounded-xl border border-stone-200 p-8 text-center">
            <ClipboardList className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">No audit logs found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => {
              const EntityIcon = getEntityIcon(log.entity_type);
              const ActionIcon = getActionIcon(log.action);
              
              return (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl border border-stone-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedLog(log)}
                  data-testid={`log-${log.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${getActionColor(log.action)}`}>
                      <ActionIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                          {log.action.toUpperCase()}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-stone-600">
                          <EntityIcon className="w-3 h-3" />
                          {log.entity_type}
                        </span>
                        {log.reason && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            Has reason
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-stone-900 mt-1 truncate">
                        {log.details?.name || log.details?.type || `ID: ${log.entity_id.slice(0, 8)}...`}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-stone-500">
                        <span>By: {log.user_name || 'Unknown'}</span>
                        {log.company_name && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {log.company_name}
                          </span>
                        )}
                        <span>{formatDate(log.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>

      {/* Log Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getActionColor(selectedLog?.action || '')}`}>
                {selectedLog?.action?.toUpperCase()}
              </span>
              {selectedLog?.entity_type}
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-stone-500">User</p>
                  <p className="font-medium">{selectedLog.user_name || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-stone-500">Date/Time</p>
                  <p className="font-medium">{formatDate(selectedLog.created_at)}</p>
                </div>
                {selectedLog.company_name && (
                  <div>
                    <p className="text-stone-500">Company</p>
                    <p className="font-medium">{selectedLog.company_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-stone-500">Entity ID</p>
                  <p className="font-medium font-mono text-xs">{selectedLog.entity_id}</p>
                </div>
              </div>

              {selectedLog.reason && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-amber-800 mb-1">Reason Provided:</p>
                  <p className="text-sm text-amber-700">{selectedLog.reason}</p>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-stone-700 mb-2">Details</p>
                <pre className="bg-stone-100 rounded-lg p-3 text-xs overflow-x-auto">
                  {JSON.stringify(selectedLog.details, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogs;
